import { usePlaybackPosition, WorkbenchContext } from '@/contexts/WorkbenchContext';
import { toHHMMSS } from '@/utils';
import { FC, useContext, useCallback, useEffect, useState } from 'react';
import pauseIcon from '@/assets/icons/pause.svg';
import playIcon from '@/assets/icons/play.svg';
import backwardIcon from '@/assets/icons/backward.svg';
import forwardIcon from '@/assets/icons/farward.svg';
import soundIcon from '@/assets/icons/sound.svg';
import styles from './MediaPlayerControls.module.scss';

const SEEK_AMOUNT_SEC = 10;

export interface MediaPlayerControlsProps {
  videoElement: HTMLVideoElement | null;
}

const MediaPlayerControls: FC<MediaPlayerControlsProps> = ({ videoElement }) => {
  const { setCurrentPosition, currentPosition } = usePlaybackPosition();
  const { isVideoPaused, setIsVideoPaused, isVideoMuted, setIsVideoMuted } =
    useContext(WorkbenchContext);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);

  useEffect(() => {
    if (!videoElement) return;
    const onLoadedMetadata = (): void => setDuration(videoElement.duration);
    const onDurationChange = (): void => setDuration(videoElement.duration);
    videoElement.addEventListener('loadedmetadata', onLoadedMetadata);
    videoElement.addEventListener('durationchange', onDurationChange);
    onLoadedMetadata();
    setVolume(videoElement.volume);
    return () => {
      videoElement.removeEventListener('loadedmetadata', onLoadedMetadata);
      videoElement.removeEventListener('durationchange', onDurationChange);
    };
  }, [videoElement]);

  const handlePlayPause = useCallback(() => {
    if (!videoElement) return;
    if (videoElement.paused) {
      videoElement.play().catch(() => {
        /* ignore play rejection */
      });
      setIsVideoPaused(false);
    } else {
      videoElement.pause();
      setIsVideoPaused(true);
    }
  }, [videoElement, setIsVideoPaused]);

  const handleBackward = useCallback(() => {
    if (!videoElement) return;
    const newTime = Math.max(0, videoElement.currentTime - SEEK_AMOUNT_SEC);
    videoElement.currentTime = newTime;
    setCurrentPosition(newTime, 'video-scrubbing');
  }, [videoElement, setCurrentPosition]);

  const handleForward = useCallback(() => {
    if (!videoElement) return;
    const dur = videoElement.duration;
    const newTime =
      Number.isFinite(dur) && dur > 0
        ? Math.min(dur, videoElement.currentTime + SEEK_AMOUNT_SEC)
        : videoElement.currentTime + SEEK_AMOUNT_SEC;
    videoElement.currentTime = Math.max(0, newTime);
    setCurrentPosition(videoElement.currentTime, 'video-scrubbing');
  }, [videoElement, setCurrentPosition]);
  const handleVolumeToggle = useCallback(() => {
    if (!videoElement) return;
    const nextMuted = !isVideoMuted;
    videoElement.muted = nextMuted;
    setIsVideoMuted(nextMuted);
  }, [videoElement, isVideoMuted, setIsVideoMuted]);

  const handleVolumeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!videoElement) return;
      const val = parseFloat(e.target.value);
      const v = Math.max(0, Math.min(1, val));
      videoElement.volume = v;
      videoElement.muted = v === 0;
      setVolume(v);
      setIsVideoMuted(v === 0);
    },
    [videoElement, setIsVideoMuted],
  );

  const handleVolumeUp = useCallback((): void => {
    if (!videoElement) return;
    const v = Math.min(1, videoElement.volume + 0.1);
    videoElement.volume = v;
    videoElement.muted = false;
    setVolume(v);
    setIsVideoMuted(false);
  }, [videoElement, setIsVideoMuted]);

  const handleVolumeDown = useCallback((): void => {
    if (!videoElement) return;
    const v = Math.max(0, videoElement.volume - 0.1);
    videoElement.volume = v;
    videoElement.muted = v === 0;
    setVolume(v);
    setIsVideoMuted(v === 0);
  }, [videoElement, setIsVideoMuted]);

  const handleProgressClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!videoElement || !Number.isFinite(videoElement.duration)) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const fraction = Math.max(0, Math.min(1, x / rect.width));
      const newTime = fraction * videoElement.duration;
      videoElement.currentTime = newTime;
      setCurrentPosition(newTime, 'video-scrubbing');
    },
    [videoElement, setCurrentPosition],
  );

  const isDisabled = !videoElement;
  const currentTime = videoElement ? currentPosition?.timeSec ?? videoElement.currentTime : 0;
  const progress = Number.isFinite(duration) && duration > 0 ? currentTime / duration : 0;
  const timeCodeCurrent = toHHMMSS(Math.floor(currentTime));
  const timeCodeDuration =
    Number.isFinite(duration) && duration >= 0 ? toHHMMSS(Math.floor(duration)) : '00:00:00';

  return (
    <div className={styles.controlsBar}>
      <div className={styles.controlsLeft}>
        <button
          type="button"
          className={styles.controlButton}
          onClick={handlePlayPause}
          disabled={isDisabled}
          aria-label={isVideoPaused ? 'Play' : 'Pause'}
        >
          <img src={isVideoPaused ? playIcon : pauseIcon} alt={isVideoPaused ? 'Play' : 'Pause'} />
        </button>
        <button
          type="button"
          className={styles.controlButton}
          onClick={handleBackward}
          disabled={isDisabled}
          aria-label="Rewind 10 seconds"
        >
          <img src={backwardIcon} alt="Rewind" />
        </button>
        <button
          type="button"
          className={styles.controlButton}
          onClick={handleForward}
          disabled={isDisabled}
          aria-label="Forward 10 seconds"
        >
          <img src={forwardIcon} alt="Forward" />
        </button>
      </div>

      <div
        className={styles.progressTrack}
        onClick={isDisabled ? undefined : handleProgressClick}
        style={{ cursor: isDisabled ? 'default' : 'pointer' }}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={progress * 100}
        aria-label="Video progress"
      >
        <div className={styles.progressFill} style={{ width: `${progress * 100}%` }} />
      </div>

      <div className={styles.timeCodes} aria-label="Time codes">
        <span className={styles.timeCodeCurrent}>{timeCodeCurrent}</span>
        <span className={styles.timeCodeSeparator}>/</span>
        <span className={styles.timeCodeDuration}>{timeCodeDuration}</span>
      </div>

      <div className={styles.controlsRight}>
        <button
          type="button"
          className={styles.controlButton}
          onClick={handleVolumeDown}
          disabled={isDisabled}
          aria-label="Volume down"
        >
          <span className={styles.volumeIcon}>−</span>
        </button>
        <div className={styles.volumeWrapper}>
          <button
            type="button"
            className={styles.controlButton}
            onClick={handleVolumeToggle}
            disabled={isDisabled}
            aria-label={isVideoMuted ? 'Unmute' : 'Mute'}
          >
            <img src={soundIcon} alt={isVideoMuted ? 'Unmute' : 'Mute'} />
          </button>
          <input
            type="range"
            className={styles.volumeSlider}
            min={0}
            max={1}
            step={0.1}
            value={isVideoMuted ? 0 : volume}
            onChange={handleVolumeChange}
            disabled={isDisabled}
            aria-label="Volume"
          />
        </div>
        <button
          type="button"
          className={styles.controlButton}
          onClick={handleVolumeUp}
          disabled={isDisabled}
          aria-label="Volume up"
        >
          <span className={styles.volumeIcon}>+</span>
        </button>
      </div>
    </div>
  );
};

export default MediaPlayerControls;
